import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Star, Trash2 } from 'lucide-react';

import { apiClient, type IdentifierDefinitionDto } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useActiveIdentifierTypes } from '@/hooks/useIdentifierTypes';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { usePatientMutations } from '@/features/patient-record/hooks/usePatientMutations';
import { identifierDefinitionLabel, identifierLabelFor } from '@/lib/identifierLabel';
import { getIdentifierFormatError } from '@/lib/identifierValidation';
import type { PersonIdentifierViewModel } from '@/models/patient';

import { CollapsibleCard, type CollapsibleCardSummaryRow } from "../_shared/CollapsibleCard";
import { ConfirmDeleteDialog } from "../_shared/ConfirmDeleteDialog";
import { useFocusOnMount } from "../_shared/useFocusOnMount";

interface IdentifiersCardProps {
  readonly guid: string;
  readonly identifiers: readonly PersonIdentifierViewModel[];
}

export function IdentifiersCard({ guid, identifiers }: IdentifiersCardProps): ReactElement {
  const { t } = useTranslation();
  const { addIdentifier, replaceIdentifiers, removeIdentifier } = usePatientMutations(guid);
  const definitionsQuery = useActiveIdentifierTypes();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PersonIdentifierViewModel | null>(null);

  const definitions = definitionsQuery.data ?? [];

  const commitRow = (next: PersonIdentifierViewModel): void => {
    const replacement = identifiers.map((entry) =>
      identityKey(entry) === identityKey(next) ? next : entry,
    );
    replaceIdentifiers.mutate(replacement, {
      onSuccess: () => {
        setEditingKey(null);
      },
    });
  };

  const commitNew = (next: PersonIdentifierViewModel): void => {
    addIdentifier.mutate(
      {
        identifierType: next.identifierType,
        label: next.label,
        value: next.value,
      },
      {
        onSuccess: () => {
          setCreating(false);
        },
      },
    );
  };

  const showEmpty = identifiers.length === 0 && !creating;
  const defaultIdentifierType = definitions[0]?.userEnteredId ?? '';

  const summaryRows: CollapsibleCardSummaryRow[] = identifiers.map((i) => ({
    label: identifierLabelFor(definitions, i.identifierType, i.label),
    value: i.value,
    filled: i.value.length > 0,
  }));

  // Add affordance lives in the card header, matching the other record sub-sections (Add diagnosis,
  // Add variant, …).
  const addButton = (
    <Button
      size="sm"
      variant="outline"
      disabled={definitions.length === 0}
      onClick={() => {
        setCreating(true);
        setEditingKey(null);
      }}
    >
      <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
      {t('patient.demographics.addIdentifier')}
    </Button>
  );

  return (
    <>
      <CollapsibleCard
        idPrefix="demographics"
        anchorId="identifiers"
        title={t('patient.demographics.identifiers')}
        count={identifiers.length}
        headerAction={creating ? undefined : addButton}
        summary={summaryRows}
      >
        {showEmpty ? (
          <p className="text-sm text-muted-foreground italic mb-3">
            {t('patient.demographics.noIdentifiers')}
          </p>
        ) : (
          <ul className="mb-3">
            {identifiers.map((identifier) => {
              const key = identityKey(identifier);
              return (
                <li key={key}>
                  {editingKey === key ? (
                    <IdentifierEditor
                      identifier={identifier}
                      definitions={definitions}
                      excludePersonGuid={guid}
                      saving={replaceIdentifiers.isPending}
                      onSave={commitRow}
                      onCancel={() => {
                        setEditingKey(null);
                      }}
                    />
                  ) : (
                    <IdentifierRow
                      identifier={identifier}
                      definitions={definitions}
                      onEdit={() => {
                        setEditingKey(key);
                      }}
                      onDelete={() => {
                        setPendingDelete(identifier);
                      }}
                    />
                  )}
                </li>
              );
            })}
            {creating && (
              <li>
                <IdentifierEditor
                  identifier={{
                    id: null,
                    identifierType: defaultIdentifierType,
                    label: null,
                    value: '',
                  }}
                  definitions={definitions}
                  excludePersonGuid={guid}
                  saving={addIdentifier.isPending}
                  onSave={commitNew}
                  onCancel={() => {
                    setCreating(false);
                  }}
                />
              </li>
            )}
          </ul>
        )}
      </CollapsibleCard>
      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          title={t('patient.demographics.confirmDeleteIdentifierTitle')}
          message={t('patient.demographics.confirmDeleteIdentifierMessage', {
            label: identifierLabelFor(definitions, pendingDelete.identifierType, pendingDelete.label),
            value: pendingDelete.value,
          })}
          onCancel={() => {
            setPendingDelete(null);
          }}
          onConfirm={() => {
            const id = pendingDelete.id;
            if (id !== null) removeIdentifier.mutate(id);
            setPendingDelete(null);
          }}
        />
      )}
    </>
  );
}

function identityKey(identifier: PersonIdentifierViewModel): string {
  return identifier.id ?? `${identifier.identifierType}:${identifier.value}`;
}

interface IdentifierRowProps {
  readonly identifier: PersonIdentifierViewModel;
  readonly definitions: readonly IdentifierDefinitionDto[];
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

function IdentifierRow({ identifier, definitions, onEdit, onDelete }: IdentifierRowProps): ReactElement {
  const { t } = useTranslation();
  const def = definitions.find((d) => d.userEnteredId === identifier.identifierType);
  const isPrimary = def?.isPrimary === true;
  const typeLabel = identifierLabelFor(definitions, identifier.identifierType, identifier.label);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onEdit();
        }
      }}
      className="group flex items-center gap-3 cursor-pointer py-2 border-b border-dashed border-border last:border-b-0 hover:bg-brand-soft/40 transition-colors -mx-1 px-1"
    >
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground leading-tight min-w-14 max-w-32 shrink-0 break-words">
        {isPrimary && (
          <Star
            className="h-3 w-3 text-amber-500 fill-amber-500"
            aria-label={t('patient.demographics.primaryIdentifier')}
          />
        )}
        {typeLabel}
      </span>
      <span className="text-[13px] text-foreground flex-1 break-all">
        {identifier.value}
      </span>
      <span className="flex items-center gap-0.5 shrink-0 opacity-70 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
          aria-label={t('patient.demographics.editIdentifier')}
          className="h-7 w-7 p-0"
        >
          <Pencil className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          aria-label={t('patient.demographics.deleteIdentifier')}
          className="h-7 w-7 p-0"
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
        </Button>
      </span>
    </div>
  );
}

interface IdentifierEditorProps {
  readonly identifier: PersonIdentifierViewModel;
  readonly definitions: readonly IdentifierDefinitionDto[];
  readonly excludePersonGuid: string;
  readonly saving: boolean;
  readonly onSave: (next: PersonIdentifierViewModel) => void;
  readonly onCancel: () => void;
}

interface DupState {
  readonly status: 'idle' | 'checking' | 'clear' | 'duplicate';
  /** Populated when status === 'duplicate' to allow display/navigation. */
  readonly personGuid?: string;
}

function IdentifierEditor({
  identifier,
  definitions,
  excludePersonGuid,
  saving,
  onSave,
  onCancel,
}: IdentifierEditorProps): ReactElement {
  const { t } = useTranslation();
  const [dup, setDup] = useState<DupState>({ status: 'idle' });
  const inputRef = useFocusOnMount<HTMLInputElement>();

  // PTV2-1327: the primitive owns the draft + dirty registration (warn on nav / view-mode / refresh
  // while unsaved, gated on the required-value / format / duplicate checks). Persistence is delegated
  // to the parent card's `onSave`, which also collapses this editor — so autosave stays OFF (an idle
  // commit would tear the row down mid-edit and fire a whole-list replace before the async dup check
  // resolves). The row is saved only by the explicit Confirm button (or the nav-guard teardown flush,
  // which commits through the same `onSave`).
  const form = useEditableDraft<PersonIdentifierViewModel>({
    initial: identifier,
    label: t('patient.demographics.identifierFormLabel', { defaultValue: 'Identifier' }),
    autosave: false,
    canSave: (d) => {
      const def = definitions.find((x) => x.userEnteredId === d.identifierType);
      const tv = d.value.trim();
      return (
        tv.length > 0 &&
        getIdentifierFormatError(def, tv, t) === null &&
        dup.status !== 'checking' &&
        dup.status !== 'duplicate'
      );
    },
    onSave: (d) => {
      onSave({ ...d, value: d.value.trim() });
    },
  });
  const { draft } = form;

  const selectedDef = useMemo(
    () => definitions.find((d) => d.userEnteredId === draft.identifierType),
    [definitions, draft.identifierType],
  );
  const placeholder = selectedDef?.formatHint ?? t('patient.demographics.identifierValuePlaceholder');
  const requiresUniqueness = selectedDef?.allowDuplicates === false;
  const trimmedValue = draft.value.trim();
  // Mirrors the BE format rule so the user sees the error inline before Save,
  // matching the pre-save behavior in the create-patient wizard (Step 0/1).
  const formatError = getIdentifierFormatError(selectedDef, trimmedValue, t);
  const canSave =
    trimmedValue.length > 0 &&
    formatError === null &&
    dup.status !== 'checking' &&
    dup.status !== 'duplicate';

  // Reset the duplicate state whenever the type or value changes.
  function onTypeChange(next: string): void {
    form.setDraft((d) => ({ ...d, identifierType: next }));
    setDup({ status: 'idle' });
  }
  function onValueChange(next: string): void {
    form.setDraft((d) => ({ ...d, value: next }));
    setDup({ status: 'idle' });
  }

  async function checkDuplicate(value: string): Promise<DupState> {
    if (
      !requiresUniqueness ||
      value.length === 0 ||
      selectedDef === undefined ||
      formatError !== null
    ) {
      return { status: 'clear' };
    }
    setDup({ status: 'checking' });
    try {
      const result = await apiClient.getIdentifierInUse({
        userEnteredId: selectedDef.userEnteredId ?? draft.identifierType,
        value,
        excludePersonGuid,
      });
      if (result.inUse) {
        const next: DupState =
          result.personGuid !== undefined && result.personGuid !== null
            ? { status: 'duplicate', personGuid: result.personGuid }
            : { status: 'duplicate' };
        setDup(next);
        return next;
      }
      const next: DupState = { status: 'clear' };
      setDup(next);
      return next;
    } catch {
      // Best-effort: don't block the save path if the dup endpoint is unreachable.
      const next: DupState = { status: 'clear' };
      setDup(next);
      return next;
    }
  }

  async function submitAndClose(): Promise<void> {
    if (trimmedValue.length === 0) return;
    if (formatError !== null) return;
    const result = await checkDuplicate(trimmedValue);
    if (result.status === 'duplicate') return;
    onSave({ ...draft, value: trimmedValue });
  }

  return (
    <form
      ref={form.containerProps.ref}
      className="flex flex-col gap-2 rounded-md border border-input bg-background p-2"
      onSubmit={(e) => {
        e.preventDefault();
        void submitAndClose();
      }}
    >
      <div className="flex items-center gap-2">
        <Select
          value={draft.identifierType}
          className="h-8 text-xs w-32 shrink-0"
          aria-label={t('patient.demographics.identifierType')}
          onChange={(event) => {
            onTypeChange(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onCancel();
            }
          }}
        >
          {definitions.map((option) => (
            <option key={option.userEnteredId} value={option.userEnteredId}>
              {identifierDefinitionLabel(option)}
            </option>
          ))}
        </Select>
        <Input
          ref={inputRef}
          value={draft.value}
          className="h-8 text-sm flex-1"
          placeholder={placeholder}
          aria-invalid={formatError !== null || dup.status === 'duplicate'}
          onChange={(event) => {
            onValueChange(event.target.value);
          }}
          onBlur={() => {
            if (formatError !== null) return;
            void checkDuplicate(trimmedValue);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onCancel();
            }
          }}
        />
      </div>
      {formatError !== null && (
        <p className="text-xs text-destructive" role="alert">
          {formatError}
        </p>
      )}
      {formatError === null && dup.status === 'checking' && (
        <p className="text-xs text-muted-foreground" role="status">
          {t('patient.demographics.identifierDuplicateChecking')}
        </p>
      )}
      {formatError === null && dup.status === 'duplicate' && (
        <p className="text-xs text-destructive" role="alert">
          {t('patient.demographics.identifierDuplicateError')}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={saving}
        >
          {t('patient.demographics.cancel')}
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!canSave}
          loading={saving}
          loadingText={t('patient.dirtyFooter.saving')}
        >
          {t('patient.demographics.confirm')}
        </Button>
      </div>
    </form>
  );
}
